import type { BorderProps, ResponsiveValue, ShadowProps } from 'styled-system';
import { border, shadow } from 'styled-system';

import type {
  BorderOverrideProps,
  GridItemProps,
  GridProps,
} from '../extensions';
import { borderShorthandOverride, grid, gridItem } from '../extensions';
import styledFactory from '../styledFactory';
import variantFactory from '../variantFactory';

export interface Props
  extends Omit<BorderProps, keyof BorderOverrideProps>,
    ShadowProps,
    BorderOverrideProps,
    GridProps,
    GridItemProps {
  variant?: ResponsiveValue<string>;
  __borderCommon__?: 'common';
}

const createBorderBoxComponent = (displayName: string, inlineBox = false) => {
  const Component = styledFactory<Props, 'div'>(
    'div',
    displayName,
    [
      variantFactory('components.borderBox', { prop: '__borderCommon__' }),
      variantFactory('components.borderBox.variants'),
    ],
    [border, borderShorthandOverride, shadow, grid, gridItem],
    'border-width: 0;',
    {
      variant: 'default',
      __borderCommon__: 'common',
      display: inlineBox ? 'inline-block' : undefined,
    },
  );

  return Component;
};

/**
 * BorderBox is a specialized Box with support for border and shadow props.
 */
const BorderBox = createBorderBoxComponent('BorderBox');

/**
 * BorderBoxInline is convenience BorderBox with inline-block display.
 */
const BorderBoxInline = createBorderBoxComponent('BorderBoxInline', true);

export default Object.assign(BorderBox, { inline: BorderBoxInline });
